import React, { useMemo, useState, useRef, Fragment } from 'react';
import { Combobox, Transition } from '@headlessui/react';

// Icons
const ChevronUpDownIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" style={{ width: '1.25rem', height: '1.25rem' }}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 15L12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9" />
  </svg>
);

const CheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" style={{ width: '1.25rem', height: '1.25rem' }}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
  </svg>
);

// 內部使用的小型 Combobox，完全複製 ModernCombobox 的邏輯
function MiniCombobox({ options, value, onChange, placeholder, className }) {
  const [query, setQuery] = useState('');
  const buttonRef = useRef(null);

  const selectedOption = options.find((opt) => opt.value === value) || null;

  const filteredOptions =
    query === ''
      ? options
      : options.filter((opt) =>
          opt.label
            .toLowerCase()
            .replace(/\s+/g, '')
            .includes(query.toLowerCase().replace(/\s+/g, ''))
        );

  return (
    <Combobox value={selectedOption} onChange={(opt) => onChange(opt ? opt.value : '')} nullable>
      {({ open }) => (
        <div style={{ position: 'relative', width: '100%' }} className={className}>
          <div
            className="modern-combobox-wrapper"
            onMouseDown={(event) => {
              if (!open && buttonRef.current) {
                // 允許點擊 wrapper 任何地方都能觸發下拉
                buttonRef.current.click();
              }
            }}
          >
            <Combobox.Input
              className="modern-combobox-input"
              displayValue={(opt) => (opt ? opt.label : '')}
              onChange={(event) => setQuery(event.target.value)}
              // 聚焦時自動展開
              onFocus={() => {
                if (!open && buttonRef.current) {
                  buttonRef.current.click();
                }
              }}
              placeholder={placeholder}
            />
            <Combobox.Button ref={buttonRef} className="modern-combobox-btn">
              <ChevronUpDownIcon aria-hidden="true" />
            </Combobox.Button>
          </div>

          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            afterLeave={() => setQuery('')}
          >
            <Combobox.Options className="modern-combobox-options" style={{ zIndex: 100 }}>
              {filteredOptions.length === 0 && query !== '' ? (
                <div className="modern-combobox-empty">
                  Not found.
                </div>
              ) : (
                filteredOptions.map((opt) => (
                  <Combobox.Option
                    key={opt.value}
                    className={({ active }) =>
                      `modern-combobox-option ${active ? 'active' : ''}`
                    }
                    value={opt}
                  >
                    {({ selected, active }) => (
                      <>
                        <span className={`option-label ${selected ? 'selected' : ''}`}>
                          {opt.label}
                        </span>
                        {selected ? (
                          <span className={`option-check ${active ? 'active' : ''}`}>
                            <CheckIcon aria-hidden="true" />
                          </span>
                        ) : null}
                      </>
                    )}
                  </Combobox.Option>
                ))
              )}
            </Combobox.Options>
          </Transition>
        </div>
      )}
    </Combobox>
  );
}

export default function ModernDatePicker({ label, value, onChange }) {
  // 拆解日期
  const [year, month, day] = useMemo(() => {
    if (!value) return ['', '', ''];
    const parts = value.split('-');
    return [parts[0] || '', parts[1] || '', parts[2] || ''];
  }, [value]);

  // 選項生成
  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const arr = [];
    for (let i = currentYear; i >= 1900; i--) {
      arr.push({ value: i.toString(), label: i.toString() });
    }
    return arr;
  }, []);

  const monthOptions = useMemo(() => 
    Array.from({ length: 12 }, (_, i) => {
      const m = (i + 1).toString().padStart(2, '0');
      return { value: m, label: m };
    }), []
  );

  const dayOptions = useMemo(() => {
    const y = parseInt(year);
    const m = parseInt(month);
    const daysInMonth = (y && m) ? new Date(y, m, 0).getDate() : 31;
    return Array.from({ length: daysInMonth }, (_, i) => {
      const d = (i + 1).toString().padStart(2, '0');
      return { value: d, label: d };
    });
  }, [year, month]);

  const handleChange = (newYear, newMonth, newDay) => {
    // 只要有改動就更新，不管是否填滿，讓父層自己判斷
    const y = newYear || '';
    const m = newMonth || '';
    const d = newDay || '';
    
    // 如果年月日都有值，組合成完整日期字串
    if (y && m && d) {
      onChange(`${y}-${m}-${d}`);
    } else if (!y && !m && !d) {
      onChange(''); // 全清空
    } else {
      // 部分填寫時，暫存或保持原樣比較麻煩，
      // 這裡採用簡單策略：如果用戶在選，我們就更新部分值到字串
      // 但標準日期格式需要完整，所以這裡我們只在「完整」時才送出標準格式
      // 或者：如果這是一個受控組件，我們需要某種方式保存中間狀態
      // 既然上層邏輯是存字串，我們這裡可以暫時只允許完整輸入才 onChange
      // 但為了 UX，我們允許部分更新，只是對於父層來說可能不是有效日期
      
      // 修正策略：如果尚未完整，我們仍然回傳部分組合，
      // 例如 "2000-01-" 或 "2000--01"，讓父層解析或保留狀態
      onChange(`${y}-${m}-${d}`);
    }
  };

  return (
    <div className="input-group">
      {label && <label>{label}</label>}
      <div style={{ display: 'flex', flexDirection: 'row', gap: '0.5rem', width: '100%' }}>
        <div style={{ flex: 3 }}>
          <MiniCombobox 
            options={yearOptions} 
            value={year} 
            onChange={(v) => handleChange(v, month, day)} 
            placeholder="Year"
          />
        </div>
        <div style={{ flex: 2 }}>
          <MiniCombobox 
            options={monthOptions} 
            value={month} 
            onChange={(v) => handleChange(year, v, day)} 
            placeholder="Month"
          />
        </div>
        <div style={{ flex: 2 }}>
          <MiniCombobox 
            options={dayOptions} 
            value={day} 
            onChange={(v) => handleChange(year, month, v)} 
            placeholder="Day"
          />
        </div>
      </div>
    </div>
  );
}
